import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resetPassword } from '../../services/auth';
import { Helmet } from 'react-helmet';

const RecuperarPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Por favor, ingresa tu correo electrónico.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await resetPassword(email);
      setSuccessMessage(
        '¡Listo! Hemos enviado un enlace a tu correo para restablecer tu contraseña. Revisa tu bandeja de entrada o spam.'
      );
    } catch (err) {
      console.error('Error al solicitar recuperación:', err);
      setError(
        err.message || 'No se pudo enviar el correo de recuperación. Verifica tu email e intenta nuevamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20 relative bg-dark-grid">
      <Helmet>
        <title>Recuperar Contraseña - MatiMarket</title>
        <meta name="description" content="Recupera el acceso a tu cuenta de MatiMarket de forma rápida y segura." />
      </Helmet>

      {/* Decorative background glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10 glass-card p-8 rounded-2xl border border-gold/30 shadow-2xl backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/10 border border-gold/40 mb-4 text-gold shadow-lg shadow-gold/20">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-white uppercase tracking-wider">
            Recuperar <span className="text-gold-gradient">Contraseña</span>
          </h1>
          <p className="text-sm text-gray-300 mt-2">
            Ingresa tu correo asociado y te enviaremos las instrucciones de recuperación segura.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-sm flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-sm">
            <div className="font-semibold mb-1 flex items-center gap-2 text-emerald-300">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              ¡Correo enviado!
            </div>
            {successMessage}
          </div>
        )}

        {!successMessage ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="recovery-email" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <input
                  id="recovery-email"
                  type="email"
                  required
                  placeholder="ejemplo@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 px-4 rounded-xl font-bold uppercase tracking-wider text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 shadow-lg shadow-gold/25 hover:shadow-gold/40 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  <span>Enviando enlace...</span>
                </>
              ) : (
                'Enviar Enlace de Recuperación'
              )}
            </button>
          </form>
        ) : (
          <div className="mt-6 text-center">
            <Link
              to="/ingresar"
              className="inline-block py-3 px-6 rounded-xl font-semibold text-sm text-gold border border-gold/40 hover:bg-gold/10 transition"
            >
              Regresar al Inicio de Sesión
            </Link>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-800 text-center text-sm text-gray-400">
          ¿Recordaste tu contraseña?{' '}
          <Link to="/ingresar" className="text-gold font-semibold hover:underline">
            Inicia sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RecuperarPassword;
